/**
 * Item Group Utilities
 *
 * Helper functions for managing itemGroups which are scoped to category+subCategory.
 * ItemGroups are derived from items collection, not stored in settings.
 */

import type { Item } from '../types';

/**
 * Reserved itemGroup values that users cannot create.
 * "None" is used as the default/empty value.
 */
export const RESERVED_ITEM_GROUPS = ['None', 'none', 'NONE'];

/**
 * Get unique itemGroups for a category+subCategory combination.
 * Returns sorted array of group names (excluding reserved values).
 */
export function getItemGroupsForCategoryAndSubCategory(
  items: Item[],
  category: string | null | undefined,
  subCategory: string | null | undefined
): string[] {
  if (!category) return [];

  const groups = new Set<string>();

  for (const item of items) {
    // Match category and subCategory (treat null/undefined/'None' as equivalent for matching)
    const itemCat = item.category || 'None';
    const itemSubCat = item.subCategory || 'None';
    const targetCat = category || 'None';
    const targetSubCat = subCategory || 'None';

    if (
      itemCat === targetCat &&
      itemSubCat === targetSubCat &&
      item.itemGroup &&
      !RESERVED_ITEM_GROUPS.includes(item.itemGroup)
    ) {
      groups.add(item.itemGroup);
    }
  }

  return Array.from(groups).sort();
}

/**
 * Generate unique suffix for orphaned itemGroups when category/subCategory is deleted.
 * Uses base-36 encoding of minutes since epoch for compact, unique suffix.
 */
export function generateOrphanedGroupSuffix(): string {
  return Math.floor(Date.now() / 60000).toString(36);
}

/**
 * Validate itemGroup name.
 * Returns true if the name is valid (not empty, not reserved).
 */
export function isValidItemGroupName(name: string | null | undefined): boolean {
  if (!name || name.trim() === '') return false;
  return !RESERVED_ITEM_GROUPS.includes(name.trim());
}

/**
 * Build the composite group key for display in GroupBy.
 * Format: "Category > SubCategory > ItemGroup"
 */
export function buildItemGroupDisplayKey(
  category: string | null | undefined,
  subCategory: string | null | undefined,
  itemGroup: string | null | undefined
): string {
  const cat = category || 'None';
  const subCat = subCategory || 'None';
  const group = itemGroup || 'None';
  return `${cat} > ${subCat} > ${group}`;
}
